// src/encounter/lib/to-ips-sections.ts
// Map the aggregate PatientSummary → the DS InternationalPatientSummary `sections`.
// Section identity mirrors Flutter (app_enum.dart:39-49); order + titles follow the E2
// pre-encounter frame (1736:156432, PROD-1588 wave S2 — see the sections array). Reads
// coded values defensively (code.display | code.displayName | code.code) so minor wire
// casing differences degrade to a usable label rather than crashing.
//
// Provenance is intentionally OMITTED: the legacy summary items carry no
// verified/self-reported field (PROD-1157 parity follow-up). We never fabricate it.
//
// D3 BLOOD GROUP — DATA-GATED, NOT MOUNTED (Samer decision strip 2026-07-22, PROD-1588
// cure wave 7). The ruling: "BUILD — blood-group rail section per node 613:2009 …
// verify the wire serves blood group before rendering values." VERIFIED 2026-07-22
// against Scrips.Patient origin/master (f68b66db): `BloodGroup` exists ONLY on the DB
// entity (Domain/Entities/Patient.cs:30 + its EF mapping) — NO DTO, controller, or
// projection serves it; GetPatientSummary / patient detail carry nothing; no in-flight
// PR adds it (gh pr list --search BloodGroup: empty). Per the anti-fiction clause the
// DS `bloodGroupIpsSection` (1.53) is NOT composed here — the rail simply omits the
// section. Unblocks the moment a wire field ships: build the section from the REAL
// value via bloodGroupIpsSection({ group, verified }) and append it below.
import type { IpsSection, IpsEntry } from '@scripsteam/scrips-signal-ds';
import type {
  PatientAllergy,
  PatientSummary,
  SummaryCode,
} from '@/types/patient-summary-api';
import { clinicalDisplayName } from '@/lib/clinical-display-name';
import { medicationDoseLine } from './medication-dose-line';

// Name SOURCE = the one shared clinical formatter (PROD-1656 Phase A). For today's
// SummaryCode wire ({code, display, displayName}) it resolves exactly the old chain
// (display || displayName || code) — zero visual change — and starts preferring the
// dictionary `preferredDisplay` / FHIR `text` the moment the BE serves them.
function label(code: SummaryCode | null | undefined): string {
  return clinicalDisplayName(code).name;
}

/** Free text the backend writes, or undefined when there is none (empty, or its own "n/a" placeholder). */
function freeText(text: string | null | undefined): string | undefined {
  const t = text?.trim();
  return t && t.toLowerCase() !== 'n/a' ? t : undefined;
}

function codeText(code: SummaryCode | null | undefined): string | undefined {
  return clinicalDisplayName(code).code;
}

function notDeleted<T extends { isDeleted?: boolean | null }>(items: T[] | null | undefined): T[] {
  return (items ?? []).filter((i) => !i.isDeleted);
}

/**
 * De-duplicate a clinical-summary section by its coded value — mirrors Flutter's
 * `uniqueItems<T extends CodeMixin>` (patient_summary.dart:178 / encounter_bloc.dart:891), which
 * keeps the FIRST occurrence per distinct `displayCode`. The aggregate GetPatientSummary can carry
 * the SAME coded entry hundreds of times (e.g. ~495 flag rows for one patient — PROD-1271); without
 * this the React rail renders every duplicate while Flutter shows only the unique set (~5). Flutter
 * dedupes Flags (by flagResponse.code), Allergies/Medications/Conditions/Procedures (by code.code).
 *
 * Divergence (deliberate, safer than Flutter): a row with NO code is ALWAYS kept (keyed by its
 * unique loop index), whereas Flutter's `displayCode ?? ""` collapses every empty-code row into
 * one — which would silently hide codeless entries. The real duplicates all carry codes, so this
 * still fully collapses the bloat. The index key can't collide with a coded key (`idx:` vs `c:`)
 * nor with another code-less row (index is unique per iteration) — Codex review, PROD-1271.
 */
function dedupeByCode<T>(
  items: T[],
  codeOf: (item: T) => string | number | null | undefined,
): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  items.forEach((item, i) => {
    const raw = codeOf(item);
    const code = raw === null || raw === undefined ? '' : String(raw).trim();
    const key = code !== '' ? `c:${code}` : `idx:${i}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(item);
    }
  });
  return out;
}

// NO per-section render cap — Flutter lists every entry in the pre-encounter rail (no "+N more"
// fold), and its section count is the true total. The earlier PROD-1158 cap (8 rows + a synthetic
// "+N more" trailer) diverged from Flutter on both the listing AND the count (the trailer was
// counted as an entry → "9" instead of the real 20). The 491-row scroll that motivated PROD-1158 is
// now addressed at the source by dedupeByCode (Flutter uniqueItems parity, PROD-1271), so each
// section is bounded by its distinct-code count. Flutter has no disclosure affordance here either
// (it lists all), so "no fold" is the parity target. If progressive disclosure is ever wanted it
// belongs in the DS organism as a future `maxVisible`-style expander (no such prop today) — NOT a
// consumer-side truncation that drops data + skews the count badge. (PROD-1158 superseded.)

/**
 * The allergies the rail lists, in its order: not deleted, one per coded allergen (the first). Exported
 * so a note's chart quote of the allergy list (to-chart-snapshot.ts) reads exactly this list — one
 * source per fact, never a second filter that could drop an allergy the rail shows.
 */
export function railAllergies(summary: PatientSummary | null | undefined): PatientAllergy[] {
  return dedupeByCode(notDeleted(summary?.allergies), (a) => a.code?.code);
}

/** An allergy's substance name, as the rail writes it. */
export function allergyName(a: PatientAllergy): string {
  return label(a.code) || 'Allergy';
}

/** An allergy's criticality, as recorded (empty when none). */
export function allergyCriticality(a: PatientAllergy): string {
  return label(a.criticality);
}

/**
 * Build the 9 IPS sections in canonical order. Empty sections are still emitted
 * (entries: []) so the DS renders the "No X on file" empty row + the count.
 */
export function patientSummaryToIpsSections(
  summary: PatientSummary | null | undefined,
  /** `familyRelation` (`?x=rail-fixes` on the rail-v3 rail; Samer, 3 Nov): a family history row reads the relative first, then the
   *  conditions and the free text written with them ("Father · Unstable angina"), as Flutter showed it (clinical_info_sub_item_widget
   *  .dart:430) and the catalog's rail row draws it. Absent: the conditions only, as before. */
  opts?: { familyRelation?: boolean },
): IpsSection[] {
  const s = summary ?? {};

  // Dedupe by code first (Flutter uniqueItems parity, PROD-1271) — collapses the duplicate rows the
  // aggregate summary carries (e.g. flag code 1×6, 9×4) so the rail matches Flutter's unique set.
  const flags: IpsEntry[] = dedupeByCode(
    notDeleted(s.flags),
    (f) => f.flagResponse?.code,
  ).map((f, i) => ({
    id: f.identifier || `flag-${i}`,
    primary: (f.flagResponse?.display || 'Flag').trim(),
  }));

  const allergies: IpsEntry[] = railAllergies(s).map((a, i) => {
    const category = typeof a.category === 'string' ? a.category : label(a.category);
    return {
      id: a.identifier || `allergy-${i}`,
      primary: allergyName(a),
      secondary: label(a.criticality) || category || undefined,
    };
  });

  // Stopped meds (status "stop", Flutter medicationStatusStop) STAY listed — Flutter shows them
  // with a status marker, not hidden (active_medication_row.dart badges; the pre-encounter widget
  // lists summary meds unfiltered). Live-verified (PROD-1493 smoke): the aggregate keeps returning
  // a stopped med with status "stop". Reflection = a "Stopped" secondary prefix + actions: []
  // (an explicitly-empty action set the rail respects — no Edit/Stop on an already-stopped row).
  const medications: IpsEntry[] = dedupeByCode(
    notDeleted(s.medication),
    (m) => m.code?.code,
  ).map((m, i) => {
    const stopped = (m.status ?? '').trim().toLowerCase() === 'stop';
    const dose = medicationDoseLine(m);
    return {
      id: m.identifier || `med-${i}`,
      primary: label(m.code) || 'Medication',
      code: codeText(m.code),
      secondary: stopped ? ['Stopped', dose].filter(Boolean).join(' — ') : dose,
      ...(stopped ? { actions: [] } : {}),
    };
  });

  const activeProblems: IpsEntry[] = dedupeByCode(
    notDeleted(s.activeConditions),
    (c) => c.code?.code,
  ).map((c, i) => ({
    id: c.identifier || `active-${i}`,
    primary: label(c.code) || 'Problem',
    code: codeText(c.code),
  }));

  const resolvedProblems: IpsEntry[] = dedupeByCode(
    notDeleted(s.resolvedConditions),
    (c) => c.code?.code,
  ).map((c, i) => ({
    id: c.identifier || `resolved-${i}`,
    primary: label(c.code) || 'Problem',
    code: codeText(c.code),
  }));

  const procedures: IpsEntry[] = dedupeByCode(
    notDeleted(s.procedures),
    (p) => p.code?.code,
  ).map((p, i) => ({
    id: p.identifier || `proc-${i}`,
    primary: label(p.code) || 'Procedure',
    code: codeText(p.code),
  }));

  // NOT de-duplicated (vaccinations / family history / social history): Flutter's `uniqueItems`
  // is applied only to Flags/Allergies/Medications/Conditions/Procedures — not these. Deliberate
  // parity: e.g. two administrations of the same vaccine code are distinct rows, not a duplicate.
  const vaccinations: IpsEntry[] = notDeleted(s.immunization).map((v, i) => ({
    id: v.identifier || `vax-${i}`,
    primary: label(v.vaccine) || 'Vaccination',
    code: codeText(v.vaccine),
  }));

  const familyHistory: IpsEntry[] = notDeleted(s.familyHistory).map((h, i) => {
    const codes = Array.isArray(h.code) ? h.code : h.code ? [h.code] : [];
    const primary = codes.map(label).filter(Boolean).join(', ');
    if (!opts?.familyRelation) return { id: h.identifier || `fam-${i}`, primary: primary || 'Family history' };
    // The conditions, each with its own free text in brackets, then the record's note. A code the backend marked deleted is out.
    const live = codes.filter((c) => !(c as { isDeleted?: boolean | null }).isDeleted);
    const conditions = live
      .map((c) => {
        const name = label(c);
        const text = freeText(c.description);
        return name && text ? `${name} (${text})` : name || text;
      })
      .filter(Boolean)
      .join(', ');
    const words = [conditions, freeText(h.note)].filter(Boolean).join(' · ');
    const relative = h.relationship?.display?.trim();
    return {
      id: h.identifier || `fam-${i}`,
      primary: relative || words || 'Family history',
      ...(relative && words ? { secondary: words } : {}),
    };
  });

  const socialHistory: IpsEntry[] = notDeleted(s.socialHistory).map((h, i) => {
    const codeLabel = typeof h.code === 'string' ? h.code : label(h.code);
    return {
      id: h.identifier || `soc-${i}`,
      primary: (codeLabel || 'Social history').trim(),
      secondary: h.value || undefined,
    };
  });

  // Section order + titles per the E2 pre-encounter frame (1736:156432 — the spec picture,
  // PROD-1588 wave S2): Patient Flags → Allergies → problems → Past Surgical History →
  // Medications → Vaccinations → Family History → Social History. (The earlier order put
  // Medications before the problem/history segments — Samer's "cluster by segment" reject.)
  const sections: IpsSection[] = [
    { id: 'flags', title: 'Patient Flags', accent: 'warning', entries: flags },
    { id: 'allergies', title: 'Allergies', accent: 'error', entries: allergies },
    { id: 'active-problems', title: 'Active Problems', entries: activeProblems },
    { id: 'resolved-problems', title: 'Resolved Problems', entries: resolvedProblems },
    { id: 'procedures', title: 'Past Surgical History', entries: procedures },
    // Medications carries the warning accent per PreEncounter.dc.html (its `tone: 'warning'`).
    // Its POSITION deliberately does NOT follow that file: the design lists Medications right
    // after Allergies, which is the exact ordering Samer rejected above ("cluster by segment").
    // A design that predates the reject does not silently revert it.
    { id: 'medications', title: 'Medications', accent: 'warning', entries: medications },
    // "Vaccinations History", not "Vaccinations" — the source's own section title
    // (`ui_data.dart:105`, `patientClinicalUiItems()`), and what the deployed banner draws. The
    // title is section DATA, so this corrects the in-encounter banner and the pre-encounter rail
    // together; they read the same array and there is no reason for them to disagree with the
    // source. ORDER is untouched — see the Samer reject recorded just above.
    { id: 'vaccinations', title: 'Vaccinations History', entries: vaccinations },
    { id: 'family-history', title: 'Family History', entries: familyHistory },
    { id: 'social-history', title: 'Social History', entries: socialHistory },
  ];
  // List every entry (Flutter parity) — no fold/cap. Dedup (above) bounds each section to its
  // distinct-code count, so the count badge + the listing now match Flutter.
  return sections;
}

/**
 * PROD-2804 / R6 — which aggregate-summary FIELD feeds which rail section.
 *
 * `patientSummaryToIpsSections` reads every field through `?? []`, so a summary that OMITS a field
 * (absent, `null`, or not a list) produced the SAME empty section as one that returned an explicit
 * empty list — and the rail then wrote "No allergies recorded" for a list nobody had read. Only an
 * explicit empty ARRAY is a successful empty read. Server-side every list is built non-null today
 * (Scrips.Patient `GetPatientSummary` and its list services), so on a healthy read nothing here
 * fires; it exists so that a payload regression degrades to "unavailable", never to a false empty.
 */
export const SUMMARY_FIELD_BY_SECTION = {
  flags: 'flags',
  allergies: 'allergies',
  'active-problems': 'activeConditions',
  'resolved-problems': 'resolvedConditions',
  procedures: 'procedures',
  medications: 'medication',
  vaccinations: 'immunization',
  'family-history': 'familyHistory',
  'social-history': 'socialHistory',
} as const satisfies Record<string, keyof PatientSummary>;

/**
 * The ids of the sections whose field the summary did not deliver as a list. A missing summary
 * (`null` / `undefined` body on a SUCCESSFUL read) delivers none of them, so every section is
 * unavailable. Call only once the read has settled; a pending read is the host's loading state.
 */
export function unavailableSummarySections(summary: PatientSummary | null | undefined): string[] {
  return Object.entries(SUMMARY_FIELD_BY_SECTION)
    .filter(([, field]) => !Array.isArray(summary?.[field]))
    .map(([id]) => id);
}
